import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Books, Barbell, Lightning, Timer, ArrowsClockwise, Heart, Trophy, MagnifyingGlass,
  UploadSimple, Plus, X, VideoCamera, Image as ImageIcon, PencilSimple, Trash, Play,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { normalizeVideoUrl, isVideoHostUrl } from "@/lib/videoUrl";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: "all",            label: "All",            icon: Books },
  { id: "mobility",       label: "Mobility",       icon: ArrowsClockwise },
  { id: "strength",       label: "Strength",       icon: Barbell },
  { id: "power",          label: "Power",          icon: Lightning },
  { id: "speed",          label: "Speed",          icon: Timer },
  { id: "agility",        label: "Agility",        icon: ArrowsClockwise },
  { id: "conditioning",   label: "Conditioning",   icon: Heart },
  { id: "sport_specific", label: "Sport-Specific", icon: Trophy },
];

// Convert a stored URL to something the browser can display.
// Backend paths look like `/api/files/edge-training/test-library/<coach>/<uuid>.<ext>`
// External URLs (YouTube, Loom, etc.) get normalized to their embed form.
const resolveMedia = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return normalizeVideoUrl(u);
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
};

const isYouTube = isVideoHostUrl;

export default function TestLibrary() {
  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create new
  const [denied, setDenied] = useState(false);

  const load = () => {
    api.get("/coach/test-library").then(({ data }) => {
      setRows(data || []);
      setDenied(false);
    }).catch((e) => {
      if (e?.response?.status === 403) setDenied(true);
    });
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (cat !== "all" && r.category !== cat) return false;
      if (q && !((r.name || "").toLowerCase().includes(q.toLowerCase()) || (r.sport || "").toLowerCase().includes(q.toLowerCase()))) return false;
      return true;
    });
  }, [rows, cat, q]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((r) => { (g[r.category] ||= []).push(r); });
    return g;
  }, [filtered]);

  if (denied) {
    return (
      <div className="p-10 max-w-3xl mx-auto">
        <div className="border border-red-500/40 bg-red-500/[0.05] p-6" data-testid="test-library-denied">
          <div className="label-tiny text-red-500 mb-1">// ACCESS RESTRICTED</div>
          <h1 className="font-heading text-3xl mb-2">Independent Coach only</h1>
          <p className="text-text-secondary text-sm">
            The assessment Test Library is a private toolkit for independent coaches.
            Academy members should use their organization&rsquo;s materials.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10 space-y-6" data-testid="test-library-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny text-brand-accent">// COACH TOOLKIT</div>
          <h1 className="font-heading text-4xl sm:text-5xl">Test Library</h1>
          <p className="text-text-secondary text-sm mt-1">
            Standard testing protocols with reference videos &middot; add your own to build a personal exercise database.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setEditorOpen(true); }}
          className="inline-flex items-center gap-2 bg-brand-accent text-surface-base px-4 py-2 text-sm label-tiny tracking-wider hover:opacity-90 transition"
          data-testid="test-lib-add-btn"
        >
          <Plus size={14} weight="bold" /> ADD CUSTOM TEST
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 border-b border-white/10 pb-3" data-testid="test-lib-filters">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search tests..."
            className="pl-7"
            data-testid="test-lib-search"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCat(c.id)}
              data-testid={`test-lib-cat-${c.id}`}
              className={`inline-flex items-center gap-1 px-3 py-1.5 text-xs label-tiny tracking-wider border transition ${
                cat === c.id
                  ? "bg-brand-accent text-surface-base border-brand-accent"
                  : "text-text-secondary border-white/10 hover:border-white/30"
              }`}
            >
              <c.icon size={12} weight="duotone" /> {c.label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Grouped grid */}
      {Object.keys(grouped).length === 0 ? (
        <div className="border border-white/10 p-10 text-center text-text-secondary" data-testid="test-lib-empty">
          No tests match your filters yet.
        </div>
      ) : (
        Object.entries(grouped).map(([category, list]) => (
          <div key={category} className="space-y-3" data-testid={`test-lib-group-${category}`}>
            <div className="label-tiny text-brand-accent tracking-widest">// {category.replace("_", " ").toUpperCase()}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {list.map((t) => (
                <TestCard
                  key={t.test_id}
                  test={t}
                  onOpen={() => setSelected(t)}
                  onEdit={() => { setEditing(t); setEditorOpen(true); }}
                  onDelete={async () => {
                    if (!window.confirm(`Delete "${t.name}"?`)) return;
                    try {
                      await api.delete(`/coach/test-library/${t.test_id}`);
                      toast.success("Deleted");
                      load();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || "Delete failed");
                    }
                  }}
                />
              ))}
            </div>
          </div>
        ))
      )}

      {selected && <TestDetailModal test={selected} onClose={() => setSelected(null)} />}
      {editorOpen && (
        <TestEditor
          initial={editing}
          onClose={() => { setEditorOpen(false); setEditing(null); }}
          onSaved={() => { setEditorOpen(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TestCard({ test, onOpen, onEdit, onDelete }) {
  const mine = !!test.owner_coach_id;
  return (
    <div
      className="border border-white/10 bg-white/[0.02] hover:border-white/20 transition group"
      data-testid={`test-card-${test.test_id}`}
    >
      <button
        onClick={onOpen}
        className="w-full text-left p-4 space-y-2"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="font-heading text-lg">{test.name}</div>
          {mine && (
            <span className="text-[9px] label-tiny bg-brand-accent/20 text-brand-accent px-1.5 py-0.5">CUSTOM</span>
          )}
        </div>
        <div className="text-xs text-text-secondary">
          {test.category?.replace("_", " ").toUpperCase()}
          {test.sport ? <span className="ml-2 text-brand-accent">&middot; {test.sport}</span> : null}
        </div>
        <div className="flex gap-2 pt-1">
          {test.video_url && <span className="inline-flex items-center gap-1 text-[10px] label-tiny text-brand-accent"><VideoCamera size={10} weight="fill" /> VIDEO</span>}
          {test.image_url && <span className="inline-flex items-center gap-1 text-[10px] label-tiny text-brand-accent"><ImageIcon size={10} weight="fill" /> IMAGE</span>}
          {test.protocol && <span className="text-[10px] label-tiny text-text-secondary">PROTOCOL</span>}
        </div>
      </button>
      {mine && (
        <div className="flex border-t border-white/10 divide-x divide-white/10 opacity-70 group-hover:opacity-100 transition">
          <button onClick={onEdit} data-testid={`test-card-${test.test_id}-edit`} className="flex-1 py-1.5 text-[10px] label-tiny text-text-secondary hover:text-brand-accent inline-flex items-center justify-center gap-1">
            <PencilSimple size={10} /> EDIT
          </button>
          <button onClick={onDelete} data-testid={`test-card-${test.test_id}-delete`} className="flex-1 py-1.5 text-[10px] label-tiny text-text-secondary hover:text-red-500 inline-flex items-center justify-center gap-1">
            <Trash size={10} /> DELETE
          </button>
        </div>
      )}
    </div>
  );
}

function TestDetailModal({ test, onClose }) {
  const videoUrl = test.video_url ? resolveMedia(test.video_url) : "";
  const imageUrl = test.image_url ? resolveMedia(test.image_url) : "";
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-surface-DEFAULT border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="test-detail-modal"
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="label-tiny text-brand-accent">// {(test.category || "").replace("_", " ").toUpperCase()}{test.sport ? ` · ${test.sport}` : ""}</div>
            <h2 className="font-heading text-2xl">{test.name}</h2>
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-white" data-testid="test-detail-close"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {videoUrl && (
            <div className="aspect-video bg-black/40 border border-white/10">
              {videoUrl.includes("results?search_query") ? (
                <a href={videoUrl} target="_blank" rel="noreferrer" className="w-full h-full flex flex-col items-center justify-center gap-2 group" data-testid="test-detail-watch-youtube">
                  <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play size={26} weight="fill" className="text-white ml-1" />
                  </div>
                  <span className="label-tiny text-white tracking-widest">WATCH ON YOUTUBE</span>
                </a>
              ) : isYouTube(videoUrl) ? (
                <iframe src={videoUrl} title={test.name} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-testid="test-detail-iframe" />
              ) : (
                <video src={videoUrl} controls className="w-full h-full" data-testid="test-detail-video" />
              )}
            </div>
          )}
          {imageUrl && (
            <img src={imageUrl} alt={test.name} className="w-full max-h-[400px] object-contain border border-white/10 bg-black/20" data-testid="test-detail-image" />
          )}
          {test.protocol && (
            <div>
              <div className="label-tiny text-text-secondary mb-1">PROTOCOL</div>
              <div className="text-sm whitespace-pre-wrap">{test.protocol}</div>
            </div>
          )}
          {test.notes && (
            <div>
              <div className="label-tiny text-text-secondary mb-1">NOTES</div>
              <div className="text-sm whitespace-pre-wrap">{test.notes}</div>
            </div>
          )}
          {!videoUrl && !imageUrl && !test.protocol && !test.notes && (
            <div className="text-sm text-text-secondary">No media or protocol added yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function TestEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || { category: "strength", name: "", sport: "", protocol: "", video_url: "", image_url: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const imgRef = useRef(null);

  const upload = async (target) => {
    const inputEl = target === "video" ? fileRef.current : imgRef.current;
    const f = inputEl?.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/coach/test-library/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "video") setForm((s) => ({ ...s, video_url: data.url }));
      else setForm((s) => ({ ...s, image_url: data.url }));
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputEl) inputEl.value = "";
    }
  };

  const submit = async () => {
    if (!form.name?.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      // Normalize any pasted YouTube/Vimeo URL to its embeddable form so the
      // detail modal iframe can render it without a manual fix.
      const rawVideo = (form.video_url || "").trim();
      const normalizedVideo = rawVideo ? normalizeVideoUrl(rawVideo) : "";
      const payload = {
        category: form.category,
        name: form.name.trim(),
        sport: form.sport || undefined,
        protocol: form.protocol || undefined,
        video_url: normalizedVideo || undefined,
        image_url: form.image_url || undefined,
        notes: form.notes || undefined,
      };
      if (initial?.test_id) {
        await api.patch(`/coach/test-library/${initial.test_id}`, payload);
        toast.success("Test updated");
      } else {
        await api.post("/coach/test-library", payload);
        toast.success("Custom test added");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-surface-DEFAULT border border-white/10 max-w-xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="test-editor-modal">
        <div className="flex items-center justify-between">
          <div className="font-heading text-2xl">{initial ? "Edit Test" : "New Custom Test"}</div>
          <button onClick={onClose} className="text-text-secondary hover:text-white" data-testid="test-editor-close"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-tiny text-[10px] text-text-secondary block mb-1">CATEGORY</label>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 px-3 py-2 text-sm" data-testid="test-editor-category">
              {CATEGORIES.filter((c) => c.id !== "all").map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-tiny text-[10px] text-text-secondary block mb-1">SPORT (OPTIONAL)</label>
            <Input value={form.sport || ""} onChange={(e) => setForm({ ...form, sport: e.target.value })} placeholder="e.g. boxing" data-testid="test-editor-sport" />
          </div>
        </div>

        <div>
          <label className="label-tiny text-[10px] text-text-secondary block mb-1">NAME</label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Test name (e.g. 10m Sprint)" data-testid="test-editor-name" />
        </div>

        <div>
          <label className="label-tiny text-[10px] text-text-secondary block mb-1">PROTOCOL</label>
          <Textarea rows={3} value={form.protocol || ""} onChange={(e) => setForm({ ...form, protocol: e.target.value })} placeholder="How to warm up, attempts, rest, best-attempt rules..." data-testid="test-editor-protocol" />
        </div>

        <div>
          <label className="label-tiny text-[10px] text-text-secondary block mb-1">
            REFERENCE VIDEO <span className="text-brand-heat/80">— PASTE ANY YOUTUBE OR VIMEO LINK</span>
          </label>
          <div className="flex gap-2">
            <Input
              value={form.video_url || ""}
              onChange={(e) => setForm({ ...form, video_url: e.target.value })}
              placeholder="e.g. https://youtu.be/xyz123 or a full watch/shorts/embed URL"
              data-testid="test-editor-video-url"
            />
            <label className="inline-flex items-center gap-1 border border-white/20 px-3 py-2 text-xs label-tiny cursor-pointer hover:border-brand-accent" data-testid="test-editor-video-upload-label">
              <UploadSimple size={12} /> {uploading ? "..." : "UPLOAD"}
              <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={() => upload("video")} data-testid="test-editor-video-input" />
            </label>
          </div>
          {(() => {
            const raw = (form.video_url || "").trim();
            if (!raw) return null;
            const normalized = normalizeVideoUrl(raw);
            const isEmbed = isYouTube(normalized);
            const converted = isEmbed && normalized !== raw;
            const isUploadedPath = raw.startsWith("/api/");
            return (
              <div className="mt-2 space-y-2" data-testid="test-editor-video-preview">
                {converted && (
                  <div className="label-tiny text-[10px] text-brand-heat">
                    // AUTO-CONVERTED TO EMBED · <span className="text-text-secondary normal-case tracking-normal font-mono">{normalized}</span>
                  </div>
                )}
                {!isEmbed && !isUploadedPath && (
                  <div className="label-tiny text-[10px] text-text-secondary">
                    // NOT A YOUTUBE/VIMEO URL — link will still be saved but preview may not embed
                  </div>
                )}
                {(isEmbed || isUploadedPath) && (
                  <div className="aspect-video bg-black/40 border border-white/10 max-w-md" data-testid="test-editor-video-preview-frame">
                    {isEmbed ? (
                      <iframe
                        src={normalized}
                        title="Video preview"
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video src={`${BACKEND_URL}${raw}`} controls className="w-full h-full" />
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        <div>
          <label className="label-tiny text-[10px] text-text-secondary block mb-1">IMAGE URL / GIF (OPTIONAL)</label>
          <div className="flex gap-2">
            <Input value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Optional demo image/GIF URL" data-testid="test-editor-image-url" />
            <label className="inline-flex items-center gap-1 border border-white/20 px-3 py-2 text-xs label-tiny cursor-pointer hover:border-brand-accent" data-testid="test-editor-image-upload-label">
              <UploadSimple size={12} /> {uploading ? "..." : "UPLOAD"}
              <input ref={imgRef} type="file" accept="image/*" className="hidden" onChange={() => upload("image")} data-testid="test-editor-image-input" />
            </label>
          </div>
        </div>

        <div>
          <label className="label-tiny text-[10px] text-text-secondary block mb-1">NOTES</label>
          <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Coaching cues, common errors, safety..." data-testid="test-editor-notes" />
        </div>

        <button onClick={submit} disabled={saving || uploading} className="w-full bg-brand-accent text-surface-base py-2.5 text-sm label-tiny tracking-wider disabled:opacity-60" data-testid="test-editor-save">
          {saving ? "SAVING..." : initial ? "SAVE CHANGES" : "ADD TEST"}
        </button>
      </div>
    </div>
  );
}
